import { useQuery } from "@apollo/client";
import Description from "@mui/icons-material/Description";
import { Alert, AlertTitle, Chip, Grid, Stack, Typography } from "@mui/material";
import { ExtractionReviewGrid } from "components/Document/ExtractionReview";
import { DocumentDialogContainer } from "components/Document/shared";
import { FacilityPicker } from "components/FacilityPicker";
import { FacilityPickerFragment, WasteKind } from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";
import { useRetryDocumentExtraction } from "routes/Customer/Documents/hooks/useRetryDocumentExtraction";

import { FACILITY_SHIPPING_DOCUMENTS_FOR_SOURCE, SHIPPING_DOCUMENT_FILE } from "./api";
import {
  BILL_OF_LADING_REVIEW_SCHEMA,
  DECLARED_WASTE_KIND_LABELS,
  declaredKinds,
  toExtractionResult,
  toGridExtractionState,
  toRecordValues,
} from "./extractionReview";
import { useApplyReviewedBillOfLading } from "./useApplyReviewedBillOfLading";
import { useBillOfLadingReading } from "./useBillOfLadingReading";

export type BillOfLadingReviewDialogProps = {
  onClose: () => void;
  /**
   * Document ids to review. Only the first is used: `BillOfLadingUploadDialog`
   * uploads one file per extraction job (see its own doc comment), so this
   * list is never longer than one in practice. The array shape matches
   * `AddBillOfLadingButton`'s handoff from the analyzing dialog, which reports
   * a job's succeeded documents as a list regardless of job size.
   */
  documentIds: string[];
  /** The facility confirmed on upload; the picker here still lets it be changed. */
  facilityId: string;
  onSaved: (savedId: string) => void;
};

/**
 * What the reviewer is looking at, before the fields themselves.
 *
 * Deliberately `info` rather than `warning`: this explains, while the grid's own
 * banner is the one that asks for action on low-confidence values. Two warning
 * banners stacked would leave neither meaning anything.
 */
function ReviewIntro({ kinds }: { kinds: WasteKind[] }) {
  return (
    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
      <Alert severity="info" icon={<Description fontSize="inherit" />}>
        <AlertTitle>A bill of lading, not an EPA manifest</AlertTitle>
        {/*
         * Phrased as an instruction, matching the manifest review modal's
         * "AI parsed this manifest document. Please review the parsed values
         * and confirm." Deliberately without that modal's "(marked with a
         * sparkle)" clause: this grid draws no such marker, so naming one would
         * send the reader looking for something that is not there.
         */}
        <Typography variant="body2">
          AI read this carrier&apos;s shipping paper. Please review the values below, correct
          anything wrong, then confirm to save it as a shipping document.
        </Typography>
        {kinds.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            mt={1.5}
          >
            <Typography variant="caption" color="text.secondary">
              Declared on the document
            </Typography>
            {kinds.map((kind) => (
              <Chip
                key={kind}
                size="small"
                color="info"
                variant="outlined"
                label={DECLARED_WASTE_KIND_LABELS[kind]}
              />
            ))}
          </Stack>
        )}
      </Alert>
    </Grid>
  );
}

/**
 * Re-queue extraction for this document and re-read it when that job finishes.
 *
 * The retry runs as its own job behind the analyzing dialog, so without the
 * re-read the grid keeps showing the candidate that failed: a successful retry
 * would leave a dialog that cannot be committed until it is remounted. Both the
 * reading and the document are re-read, because the grid's failed state comes
 * from the document's own extraction status.
 */
function useRetryAndReread({
  documentId,
  tenantId,
  refetchReading,
  refetchDocument,
}: {
  documentId: string | undefined;
  tenantId: string | null | undefined;
  refetchReading: () => unknown;
  refetchDocument: () => unknown;
}) {
  const onExtractionComplete = useCallback(() => {
    void refetchReading();
    void refetchDocument();
  }, [refetchReading, refetchDocument]);

  const { retryDocumentExtractions } = useRetryDocumentExtraction({ onExtractionComplete });

  return useCallback(() => {
    if (!documentId || !tenantId) return;
    void retryDocumentExtractions(tenantId, [documentId]);
  }, [documentId, tenantId, retryDocumentExtractions]);
}

/**
 * Review a bill of lading's AI reading before writing it to a
 * `WasteShippingDocument`.
 *
 * Left pane: a facility picker (decision 4 — confirmed here again, not just
 * on upload) above the shared extraction grid. Right pane: the source PDF,
 * via `DocumentDialogContainer`'s built-in preview column (which is what
 * renders `FullScreenDocumentPreview` under its own full-screen toggle).
 */
/**
 * What an earlier review of this same file already saved into this facility —
 * the record a confirm here would replace, or null on a first review.
 *
 * Keyed on the live facilityId because the picker in the dialog chrome can
 * change it mid-review, and skipped until both ids are known. Read fresh rather
 * than from cache alone: the record has to reflect a hand-edit made since this
 * dialog last looked, or the reviewer is asked about a stale version of their
 * own work.
 */
function useSavedShippingDocument(documentId: string | undefined, facilityId: string) {
  const { data } = useQuery(FACILITY_SHIPPING_DOCUMENTS_FOR_SOURCE, {
    variables: { facilityId, sourceDocumentId: documentId ?? "" },
    skip: !documentId || !facilityId,
    fetchPolicy: "cache-and-network",
  });
  // First, not "the only one": the resolver orders oldest-first to match the
  // lookup the apply itself does, so this is the row a confirm will update.
  return data?.facility?.wasteShippingDocuments[0] ?? null;
}

export function BillOfLadingReviewDialog({
  onClose,
  documentIds,
  facilityId: initialFacilityId,
  onSaved,
}: BillOfLadingReviewDialogProps) {
  const documentId = documentIds[0];
  const [facility, setFacility] = useState<FacilityPickerFragment | null>(null);
  const facilityId = facility?.id ?? initialFacilityId;

  const {
    data: fileData,
    loading: fileLoading,
    refetch: refetchDocument,
  } = useQuery(SHIPPING_DOCUMENT_FILE, {
    variables: { id: documentId ?? "" },
    skip: !documentId,
  });
  const document = fileData?.document;

  const {
    reading,
    loading: readingLoading,
    refetch: refetchReading,
  } = useBillOfLadingReading(documentId);

  const saved = useSavedShippingDocument(documentId, facilityId);

  const handleRetry = useRetryAndReread({
    documentId,
    tenantId: document?.tenantId,
    refetchReading,
    refetchDocument,
  });

  const { confirm, applying } = useApplyReviewedBillOfLading({
    documentId: documentId ?? "",
    facilityId,
    // Sent explicitly rather than left to the server's own
    // "already saved for this source document" fallback, so the document the
    // reviewer was shown as the record and the document their confirm updates
    // cannot be two different rows.
    existingId: saved?.id,
    saved,
    onApplied: onSaved,
  });

  const { status: gridStatus, extractionError: gridError } = toGridExtractionState(
    reading,
    document
  );
  const kinds = declaredKinds(reading);
  const record = useMemo(() => toRecordValues(saved), [saved]);

  if (!documentId) {
    return (
      <DocumentDialogContainer
        open
        onClose={onClose}
        title="Review Bill of Lading Data"
        noPrimaryAction
      >
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">No document to review.</Alert>
        </Grid>
      </DocumentDialogContainer>
    );
  }

  return (
    <DocumentDialogContainer
      open
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      title="Review Bill of Lading Data"
      noPrimaryAction
      previewUrl={document?.downloadLink ?? undefined}
      fileExtension={document?.fileExtension ?? undefined}
      previewLoading={fileLoading}
      sx={{ "& .MuiDialog-paper": { height: "calc(100vh - 80px)", maxHeight: "1100px" } }}
      // Caps the form column so its own `overflowY: auto` has a height to
      // scroll inside; without it the whole body scrolled as one and the
      // document could not be moved independently of the fields it is being
      // checked against. The manifest review modal bounds its form the same
      // way. The arithmetic mirrors the dialog height above, less the title and
      // action bars.
      formContainerSx={{ maxHeight: "min(calc(100vh - 220px), 960px)" }}
    >
      <ReviewIntro kinds={kinds} />
      <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
        <FacilityPicker
          label="Facility"
          facilityQueryVariant="waste"
          textFieldProps={{ required: true }}
          value={facility ?? initialFacilityId}
          onChange={setFacility}
          disabled={applying}
          invalid={false}
          isTouched={false}
          isDirty={false}
          isValidating={false}
        />
      </Grid>
      {/*
       * The grid ends with its own confirm and cancel buttons, and the dialog
       * body stops immediately under them. Padding here rather than inside the
       * shared grid, which the profile and invoice ingests also render and
       * which sits in their own containers.
       */}
      <Grid size={{ xs: 12 }} sx={{ pb: 3 }}>
        <ExtractionReviewGrid
          schema={BILL_OF_LADING_REVIEW_SCHEMA}
          extraction={reading ? toExtractionResult(reading) : null}
          status={gridStatus}
          extractionError={gridError}
          record={record}
          onCommit={confirm}
          onCancel={onClose}
          onRetry={handleRetry}
          committing={applying || readingLoading}
        />
      </Grid>
    </DocumentDialogContainer>
  );
}
